import { addRemoveFavouritesApi } from '@api/useQueryCalls';
import Baru from '@atoms/Loan/Baru';
import LocalImage from '@atoms/LocalImage';
import RemoteImage from '@atoms/RemoteImage';
import RemoveProduct from '@atoms/RemoveProduct';
import { Text } from '@components/primitives';
import { useToast } from '@hooks';
import { logFavouriteAction } from '@molecules/TopProducts/logUtils';
import navigationConstants from '@navigation/navigationConstants';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { lockPeriodToast, unavailableToast, withdrawalSuspendedToast } from '@screens/GGBillPayments/BPUtils';
import { getStore } from '@store/storeUtils';
import { useQueryClient } from '@tanstack/react-query';
import { AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { showSuccessToast } from '@utils/toastManager';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View, TouchableOpacity, Animated } from 'react-native';
import logTilePress from './analyticsBPTile';
import styles from './styles';
import products from '../../../product';

interface ProductConfig {
  hasSubProducts?: boolean;
  hasNominals?: boolean;
  template?: string;
}

interface ProductApiData {
  [key: string]: any;
}

export function openProdOrSubProdOrNominals(
  productConfig: ProductConfig,
  navigation: NavigationProp<any>,
  productApiData: ProductApiData,
  source?: string,
): void {
  if (productConfig.hasSubProducts) {
    navigation.navigate(navigationConstants.SUB_PRODUCTS, { productConfig, productApiData, source });
  }
  if (productConfig.hasNominals) {
    navigation.navigate(navigationConstants.NOMINALS, { productConfig, productApiData, source });
  }
  if (productConfig.template) {
    navigation.navigate(productConfig.template, { productConfig, productApiData, source });
  }
}

interface BPProduct {
  id: number;
  name: string;
  iconLink?: string;
  icon: string;
  screenName: string;
  availability: boolean;
  canShowNew?: boolean;
}

interface BPProductTileProps {
  bpCategoryProducts: BPProduct;
  source: string;
  isEditEnabled: boolean;
  onPressBPTile?: () => void;
  onRemoveSuccess?: () => void;
  showFavourites?: boolean;
}

function BPProductTile({
  bpCategoryProducts,
  source,
  isEditEnabled,
  onPressBPTile,
  onRemoveSuccess,
  showFavourites,
}: BPProductTileProps) {
  const navigation = useNavigation<NavigationProp<any>>();
  const { isInLockPeriod, isSuspended, selectedBPAccount, validatedReferralCode } = getStore();
  const toast = useToast();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const { mutate: isFavouriteApi } = addRemoveFavouritesApi({
    payload: {
      categoryId: bpCategoryProducts.id,
      favorite: false,
    },
    onSuccess: () => {
      onRemoveSuccess();
      // queryClient.invalidateQueries({ queryKey: ['fetch_favourites_api'] });
      queryClient.invalidateQueries({ queryKey: ['billpayment_api'] });
    },
  });

  const handlePress = useCallback(() => {
    const productConfig = products[bpCategoryProducts.id];
    if (productConfig?.transaction_category_id) {
      logTilePress(productConfig, source, validatedReferralCode, bpCategoryProducts);
    }

    if (bpCategoryProducts.availability && bpCategoryProducts.id === -1) {
      if (showFavourites) return onPressBPTile();

      return navigation.navigate(navigationConstants.HOME, {
        screen: navigationConstants.PembayaranTab,
        params: { source: AMPLITUDE_EVENT_SOURCES?.home_page },
      });
    }

    if (selectedBPAccount.accountTypeId === 0 && (isInLockPeriod || isSuspended) && !bpCategoryProducts.availability) {
      return isInLockPeriod ? lockPeriodToast(toast, t) : withdrawalSuspendedToast(toast, t);
    }

    if (bpCategoryProducts.id !== -1) {
      if (!bpCategoryProducts.availability) {
        return unavailableToast(toast, t);
      }
      return openProdOrSubProdOrNominals(productConfig, navigation, bpCategoryProducts, source);
    }
  }, [
    bpCategoryProducts,
    source,
    navigation,
    validatedReferralCode,
    isInLockPeriod,
    isSuspended,
    selectedBPAccount,
    toast,
    t,
  ]);

  const dynamicStyles = useMemo(
    () => ({
      tileButton: [
        appStyles.borderWithShadow,
        styles.productTileButton,
        styles.enabledDisabledStyles(bpCategoryProducts),
      ],
      opacityStyle: styles.enabledDisabledOpacity(bpCategoryProducts),
    }),
    [bpCategoryProducts],
  );

  const onPressToast = () => {
    showSuccessToast(toast, t('favourites.remove'), '1', 'top', 2500);
  };

  const renderProductIcon = () => {
    // 🔹 Case 1: Add Tile
    if (bpCategoryProducts.id === -1) {
      return bpCategoryProducts?.isFavourite ? (
        bpCategoryProducts.icon
      ) : (
        <LocalImage source={bpCategoryProducts.icon as any} style={{ width: scale(32), height: scale(32) }} />
      );
    }

    // 🔹 Case 2: Regular Product
    const uri = bpCategoryProducts.iconLink || (bpCategoryProducts.icon as string);
    return <RemoteImage width={32} height={32} uri={uri} />;
  };

  const shakeAnim = React.useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isEditEnabled) {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(shakeAnim, { toValue: 1, duration: 80, useNativeDriver: true }),
          Animated.timing(shakeAnim, { toValue: -1, duration: 80, useNativeDriver: true }),
        ]),
      );
      loop.start();
      return () => loop.stop();
    }
    shakeAnim.stopAnimation();
    shakeAnim.setValue(0);
  }, [isEditEnabled]);

  const rotate = shakeAnim.interpolate({
    inputRange: [-1, 1],
    outputRange: ['-3deg', '3deg'],
  });

  const animatedStyle = {
    transform: [{ rotate }],
  };

  return (
    <Animated.View style={[styles.productTile, isEditEnabled && bpCategoryProducts.id !== -1 && animatedStyle]}>
      <Baru
        testID={`${bpCategoryProducts.id}newShow`}
        showBaru={bpCategoryProducts.canShowNew}
        customStyles={styles.baruStyle}
        animate
      />
      <RemoveProduct
        isEditable={isEditEnabled && bpCategoryProducts?.id !== -1}
        onRemove={() => {
          logFavouriteAction(false, AMPLITUDE_EVENT_SOURCES.home_page, bpCategoryProducts.id);
          isFavouriteApi();
          onPressToast();
        }}
      />
      <TouchableOpacity
        testID={`btn${bpCategoryProducts.id}`}
        disabled={isEditEnabled && bpCategoryProducts?.id !== -1}
        onPress={handlePress}
        style={dynamicStyles.tileButton}>
        <View key={bpCategoryProducts.name} style={dynamicStyles.opacityStyle}>
          {renderProductIcon()}
        </View>
      </TouchableOpacity>
      <Text testID={bpCategoryProducts.screenName} style={styles.productNameText} variant="sm-normal">
        {bpCategoryProducts.screenName}
      </Text>
    </Animated.View>
  );
}

export default React.memo(BPProductTile);
